/**
 * Reorder component module.
 * @module shared/components/Reorder
 *
 */

import React from 'react';
import {Column, DescriptiveText, Icon, Row} from '../../../shared';
import {fill} from '../../../shared/components/cssUtils';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import debug from 'debug';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components/macro';

const log = debug('circleblack:investor:reorder');

/**
 * Reorder component
 * @name Reorder
 */

const _Row = styled(Row)`
  background-color: ${props => {
    if (props.theme.dark) return props.isDragging ? 'rgb(255, 255, 255, 0.1)' : null;
    return props.isDragging ? 'rgb(0, 0, 0, 0.1)' : null;
  }};
  //transition: background-color 0.2s ease-out;
  border-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  border-style: ${props => props.dividerType === 'line' ? 'none none solid' : null};
  border-radius: 4px;
  box-sizing: border-box;
  opacity: ${props => props.isDragging ? '50%' : null};
  ${props => {
    switch (props.dividerType) {
      case 'border':
        return css`
          border: 3px solid ${props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
          margin: 5px;
          box-shadow: ${props.theme.neumorphic ? '1px 1px 2px rgba(0, 0, 0, 0.5)' : null};
        `;
      case 'line':
        return css`
          border: 1px solid ${props.theme.dark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};;
          margin: 0;
          border-radius: 0;
          border-style: ${!props.last && !props.isDragging ? 'none none solid' : 'none'};
          padding: 5px 5px 10px;
        `;
      default:
        return css`
        border: none;
      `;
    }
  }}
  
  ${fill('fill')}
`;

const _Container = styled(Column)`
  overflow: initial;
`;

log('rendering');
const Reorder = ({dataSet, displayNumber, dividerType, title}) => {
  const onDragEnd = result => {
    const {destination, source, draggableId} = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const dataKeys = dataSet.data.map(e=>e.key);
    dataKeys.splice(source.index, 1);
    dataKeys.splice(destination.index, 0, draggableId);
    dataSet.updateData(dataSet.data.map(i=>({...i, order: dataKeys.indexOf(i.key) + 1})));
  };

  const listItem = (item, index, arr) => <Draggable draggableId={item.key}
                                                    index={index}
                                                    key={item.title + '-' + index}>
    {(provided, snapshot) => (
      <_Row key={index}
            wrap={true}
            alignItems={'middle'}
            margin={'5px'}
            dividerType={dividerType}
            last={index === arr.length - 1}
            ref={provided.innerRef}
            isDragging={snapshot.isDragging}
            {...provided.draggableProps}
            {...provided.dragHandleProps}>
        {displayNumber && <Column width={'24px'}>
          {item.order}
        </Column>}
        {item.content}
        <Column width={'fit-content'}>
          <Icon svg={'reOrder'} width={'30px'} margin={'5px'}/>
        </Column>
      </_Row>
    )}
  </Draggable>;

  log('rendering');
  return <DragDropContext onDragEnd={onDragEnd}>
    <Droppable droppableId={'reorderTile-col'}>
      {provided => (
        <_Container width={'fill'} ref={provided.innerRef}
                    {...provided.droppableProps}>
          {title && <DescriptiveText fontSize={'18px'} alignment={'left'} margin={'5px'}>{title}</DescriptiveText>}
          <Column>
            {dataSet.data.map(listItem)}
            {provided.placeholder}
          </Column>
        </_Container>
      )}
    </Droppable>
  </DragDropContext>;
};

export default Reorder;

Reorder.propTypes = {
  /**
   * Data and function to create/update reordering list.
   */
  dataSet: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      /**
       * JSX of content
       */
      content: PropTypes.object,
      order: PropTypes.number.isRequired,
    })).isRequired,
    updateData: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * Boolean to show display number in list
   */
  displayNumber: PropTypes.bool,
  /**
   * Title to be used if provided
   */
  title: PropTypes.string,
  /**
   * Divider for items in list. Defaults to none.
   */
  dividerType: PropTypes.oneOf(['none', 'line', 'border']),
};
